import { useEffect, useRef, useState, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import './Modal.css'

export default function Modal({ isOpen, onClose, title, children, isBottomSheet = true, onConfirm, isLightbox = false, customWidth, overflowVisible = false, ...rest }) {
  const [dragY, setDragY] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const dragStart = useRef(null)
  const modalRef = useRef(null)

  // Закрытие по Escape + подтверждение по Enter
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'Enter' && onConfirm) {
        // Не перехватываем Enter в textarea и contenteditable
        const tag = e.target?.tagName?.toLowerCase()
        if (tag === 'textarea' || e.target?.contentEditable === 'true') return
        e.preventDefault()
        onConfirm()
      }
    }
    if (isOpen) window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [isOpen, onClose, onConfirm])

  // Блокировка скролла фона
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  // Reset drag state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setDragY(0)
      setIsDragging(false)
      dragStart.current = null
    }
  }, [isOpen])

  // Swipe-to-dismiss handlers for bottom sheet
  const handleTouchStart = useCallback((e) => {
    if (!isBottomSheet) return
    // Only start drag from the handle area or if at scroll top
    const modal = modalRef.current
    const modalBody = modal?.querySelector('.modal-body')
    const isAtTop = !modalBody || modalBody.scrollTop <= 0
    const touchY = e.touches[0].clientY
    const modalRect = modal?.getBoundingClientRect()
    const isInHandleArea = modalRect && (touchY - modalRect.top) < 60

    if (isInHandleArea || isAtTop) {
      dragStart.current = { y: touchY, scrollTop: modalBody?.scrollTop || 0 }
      setIsDragging(true)
    }
  }, [isBottomSheet])

  const handleTouchMove = useCallback((e) => {
    if (!dragStart.current || !isDragging) return
    const currentY = e.touches[0].clientY
    const delta = currentY - dragStart.current.y

    // Only allow dragging down
    if (delta > 0) {
      setDragY(delta)
      // Prevent page scroll while dragging modal
      if (delta > 10) {
        e.preventDefault()
      }
    } else {
      setDragY(0)
    }
  }, [isDragging])

  const handleTouchEnd = useCallback(() => {
    if (!isDragging) return
    if (dragY > 120) {
      // Threshold reached — close
      onClose()
    }
    // Snap back
    setDragY(0)
    setIsDragging(false)
    dragStart.current = null
  }, [dragY, isDragging, onClose])

  if (typeof document === 'undefined') return null

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="modal-backdrop"
            style={isLightbox ? { backgroundColor: 'rgba(0,0,0,0.85)' } : {}}
            initial={{ opacity: 0 }}
            animate={{ opacity: isDragging ? Math.max(0.1, 1 - dragY / 300) : 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
          />
          <div className="modal-wrapper" style={isLightbox ? { cursor: 'zoom-out' } : {}} onClick={isLightbox ? onClose : undefined}>
            {isLightbox ? (
              <>
                <button
                  onClick={onClose}
                  style={{ position: 'absolute', top: '24px', right: '24px', background: 'rgba(0,0,0,0.5)', border: 'none', color: 'white', fontSize: '24px', width: '40px', height: '40px', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1002 }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                  onClick={(e) => e.stopPropagation()}
                  style={{ cursor: 'default', display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}
                >
                  {children}
                </motion.div>
              </>
            ) : (
              <motion.div
                ref={modalRef}
                className={`modal-content ${isBottomSheet ? 'bottom-sheet' : 'center-modal'}`}
                {...rest}
                style={{
                  ...(customWidth ? { maxWidth: customWidth } : {}),
                  ...(isDragging && isBottomSheet ? { 
                    transform: `translateY(${dragY}px)`,
                    transition: 'none',
                  } : {}),
                }}
                initial={isBottomSheet ? { y: '100%' } : { scale: 0.9, opacity: 0 }}
                animate={isBottomSheet ? { y: 0 } : { scale: 1, opacity: 1 }}
                exit={isBottomSheet ? { y: '100%' } : { scale: 0.9, opacity: 0 }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                onClick={(e) => e.stopPropagation()}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                {isBottomSheet && (
                  <div className="modal-drag-handle-area">
                    <div className={`modal-drag-handle ${isDragging ? 'dragging' : ''}`} />
                  </div>
                )}
                <div className="modal-header">
                  {title && <h3 className="modal-title">{title}</h3>}
                  <button className="modal-close" onClick={onClose}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
                </div>
                <div className="modal-body" style={{ overflowY: overflowVisible ? 'visible' : 'auto' }}>
                  {children}
                </div>
              </motion.div>
            )}
          </div>
        </>
      )}
    </AnimatePresence>,
    document.body
  )
}
